import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useProjectNavigation } from "../../../hooks/useProjectNavigation";
import { socketService } from "../../../services/socket";
import { useProjects } from "../../common/contexts/ProjectContext";
import { useTags } from "../../common/contexts/TagsContext";
import { useSelection } from "../../common/contexts/SelectionContext";
import { BrainContextPanel } from "../components/BrainContextPanel";
import { ParticipantBadge } from "../../common/components/ParticipantBadge";
import { CustomDialog } from "../../common/components/CustomDialog";
import { isColorLight } from "../../../utils/participantColors";
import "./Discovery.css";
import { authFetch } from "../../../services/authFetch";

interface AIInitResult {
  success: boolean;
  error?: string;
}

interface AIIndexProgress {
  total: number;
  current: number;
  percent: number;
  status: string;
}

interface AIIndexResult {
  success?: boolean;
  indexed?: number;
  error?: string;
}

interface AIStatsResult {
  totalLogs?: number;
  indexedLogs?: number;
  percent?: number;
  projectId?: string;
  error?: string;
}

interface DiscoveryProps {
  onNavigateToLog?: (logId: string, storyId: string, highlightText?: string) => void;
  isEmbedded?: boolean;
  hideAiDetails?: boolean;
}

export const Discovery = ({
  onNavigateToLog: externalNavigateToLog,
  isEmbedded = false,
  hideAiDetails = false,
}: DiscoveryProps) => {
  const { t } = useTranslation();
  const { activeProject } = useProjects();
  const { getTagColor, cameras: allCameras } = useTags();
  const { activeProjectId: _activeProjectId } = useSelection();
  const { navigate, projectPath } = useProjectNavigation();

  // Internal navigation handler: navigate to Story Producer when no external handler
  const onNavigateToLog =
    externalNavigateToLog ||
    ((
      logId: string,
      storyId: string,
      highlightText?: string,
      citationType?: string,
      citationTc?: string,
    ) => {
      const params = new URLSearchParams();
      params.set("storyId", storyId);
      params.set("logId", logId);
      if (citationType) params.set("type", citationType);
      if (citationTc) params.set("tc", citationTc);
      navigate(projectPath(`/storyproducer?${params.toString()}`));
    });

  const [apiKey, setApiKey] = useState("");
  const [isAiReady, setIsAiReady] = useState(false);
  const [isCheckingAi, setIsCheckingAi] = useState(true);
  const [_query, _setQuery] = useState("");
  const [_isLoading, _setIsLoading] = useState(false);
  const [_indexProgress, setIndexProgress] = useState<{
    total: number;
    current: number;
    percent: number;
    status?: string;
  } | null>(null);
  const [_indexStats, setIndexStats] = useState<{
    totalLogs: number;
    indexedLogs: number;
    percent: number;
  } | null>(null);

  // Filters State
  const [filtersData, setFiltersData] = useState<{
    rundowns: any[];
    events: any[];
    stories: any[];
    participants: any[];
    tags: any[];
  }>({ rundowns: [], events: [], stories: [], participants: [], tags: [] });
  const [activeFilters, setActiveFilters] = useState<{
    rundownIds: string[];
    eventIds: string[];
    storyIds: string[];
    participantIds: string[];
    tags: string[];
  }>({ rundownIds: [], eventIds: [], storyIds: [], participantIds: [], tags: [] });
  const [showFilters, setShowFilters] = useState(true);
  const [_isConnected, setIsConnected] = useState(socketService.connected);

  // Context Capacity Estimate
  const [contextEstimate, setContextEstimate] = useState<{
    estimatedTokens: number;
    maxTokens: number;
    percent: number;
    logs: number;
    segments: number;
    hasSelection: boolean;
  } | null>(null);
  const estimateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectAll, setSelectAll] = useState(false);
  const [dataSources, setDataSources] = useState<{
    logs: boolean;
    transcripts: boolean;
    otfs: boolean;
  }>({ logs: true, transcripts: true, otfs: true });

  // Brain query mode: 'general' uses logs/trans/OTFs filters,
  // 'vtr' sends the project's whole VTR/cortes catalog as context.
  const [brainMode, setBrainMode] = useState<"general" | "vtr">("general");

  // Resizer State
  const [topPanelHeight, setTopPanelHeight] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDraggingRef.current || !containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const newHeight = e.clientY - containerRect.top;

    // Limits
    const minHeight = 100;
    const maxHeight = containerRect.height - 150; // Leave space for chat
    if (newHeight >= minHeight && newHeight <= maxHeight) {
      setTopPanelHeight(newHeight);
    }
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  // Dialog State
  const [dialogData, setDialogData] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "alert" | "confirm" | "prompt";
    onConfirm?: () => void;
    confirmText?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "alert",
  });

  // Chat Sessions State
  interface ChatSession {
    id: string;
    project_id: string;
    user_id: string;
    title: string;
    created_at: string;
    updated_at: string;
    is_pinned?: boolean;
  }
  interface ChatMessage {
    role: "user" | "assistant";
    content: string;
    citations?: any[];
    metadata?: any;
    suggestedQuestions?: string[];
  }
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [chatSidebarCollapsed, setChatSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("discovery.chatSidebarCollapsed") === "1";
    } catch {
      return false;
    }
  });
  const toggleChatSidebar = () => {
    setChatSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("discovery.chatSidebarCollapsed", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // Tree View State
  const [expandedRundownIds, setExpandedRundownIds] = useState<string[]>([]);
  const [expandedEventIds, setExpandedEventIds] = useState<string[]>([]);

  const toggleRundownExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedRundownIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleEventExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedEventIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  useEffect(() => {
    const onInitResult = (data: AIInitResult) => {
      setIsAiReady(data.success);
      if (!data.success) {
        setDialogData({
          isOpen: true,
          title: t("discovery.errorAI"),
          message: "Error initializing AI: " + data.error,
          type: "alert",
        });
      }
    };

    const onIndexProgress = (data: AIIndexProgress) => {
      setIndexProgress(data);
    };

    const onIndexResult = (data: AIIndexResult) => {
      setIndexProgress(null);
      if (data.success) {
        setDialogData({
          isOpen: true,
          title: t("discovery.indexingComplete"),
          message: t("discovery.indexedSuccess", { count: data.indexed }),
          type: "alert",
        });
      } else {
        setDialogData({
          isOpen: true,
          title: t("discovery.indexingError"),
          message: "Error: " + data.error,
          type: "alert",
        });
      }
    };

    const onStatsResult = (data: AIStatsResult) => {
      if (!data.error) {
        setIndexStats({
          totalLogs: data.totalLogs || 0,
          indexedLogs: data.indexedLogs || 0,
          percent: data.percent || 0,
        });
      }
    };

    let checkInterval: ReturnType<typeof setInterval> | null = null;
    let checkAttempts = 0;
    const MAX_ATTEMPTS = 15; // Increased to allow more time for server to stabilize

    const onStatus = (data: { hasKey: boolean }) => {
      setIsAiReady(data.hasKey);
      setIsCheckingAi(false);
      if (checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
      }
    };

    const onFiltersData = (data: any) => {
      setFiltersData(data);
    };

    const onStoriesUpdated = (data: any) => {
      if (!data) return;
      let incomingStories: any[] = [];
      if (Array.isArray(data)) {
        incomingStories = data;
      } else if (data.stories) {
        incomingStories = data.stories;
      }

      if (incomingStories.length > 0) {
        setFiltersData((prev) => {
          const newStories = [...prev.stories];
          const updateMap = new Map(incomingStories.map((s) => [s.id, s]));

          const updatedPrev = newStories.map((story) => {
            const updated = updateMap.get(story.id);
            if (updated) {
              return { ...story, ...updated };
            }
            return story;
          });

          const existingIds = new Set(prev.stories.map((s) => s.id));
          const additions = incomingStories.filter((s) => !existingIds.has(s.id));

          return { ...prev, stories: [...additions, ...updatedPrev] }; // Add new stories to the top
        });
      }
    };

    const onContextEstimate = (data: any) => {
      setContextEstimate(data);
    };

    const startChecking = () => {
      if (checkInterval) clearInterval(checkInterval);
      setIsCheckingAi(true);
      checkAttempts = 0;

      checkInterval = setInterval(() => {
        if (socketService.connected) {
          checkAttempts++;
          if (checkAttempts > MAX_ATTEMPTS) {
            if (checkInterval) clearInterval(checkInterval);
            setIsCheckingAi(false);
            return;
          }
          socketService.emit("ai:check-status", {});
        }
      }, 3000); // 3 seconds interval to reduce request flooding
    };

    const onConnect = () => {
      setIsConnected(true);
      socketService.emit("ai:check-status", {});
      startChecking();
    };

    const onDisconnect = () => setIsConnected(false);

    socketService.on("ai:init-result", onInitResult);
    socketService.on("ai:index-progress", onIndexProgress);
    socketService.on("ai:index-result", onIndexResult);
    socketService.on("ai:stats-result", onStatsResult);
    socketService.on("ai:status", onStatus);
    socketService.on("ai:filters-data", onFiltersData);
    socketService.on("stories-updated", onStoriesUpdated);
    socketService.on("ai:context-estimate", onContextEstimate);
    socketService.on("connect", onConnect);
    socketService.on("disconnect", onDisconnect);

    // Initial check on mount
    socketService.emit("ai:check-status", {});
    startChecking();

    return () => {
      if (checkInterval) clearInterval(checkInterval);
      socketService.off("ai:init-result", onInitResult);
      socketService.off("ai:index-progress", onIndexProgress);
      socketService.off("ai:index-result", onIndexResult);
      socketService.off("ai:stats-result", onStatsResult);
      socketService.off("ai:status", onStatus);
      socketService.off("ai:filters-data", onFiltersData);
      socketService.off("stories-updated", onStoriesUpdated);
      socketService.off("ai:context-estimate", onContextEstimate);
      socketService.off("connect", onConnect);
      socketService.off("disconnect", onDisconnect);
    };
  }, []);

  // Load filters data and chat sessions when project is active
  useEffect(() => {
    if (activeProject) {
      socketService.emit("ai:get-filters-data", { projectId: activeProject.id });
      socketService.emit("ai:get-stats", { projectId: activeProject.id });
      // Load chat sessions
      loadChatSessions(activeProject.id);
    }
  }, [activeProject]);

  // Request context capacity estimate when filters change (debounced)
  useEffect(() => {
    if (!activeProject?.id) return;
    if (estimateTimerRef.current) clearTimeout(estimateTimerRef.current);
    estimateTimerRef.current = setTimeout(() => {
      socketService.emit("ai:estimate-context-size", {
        projectId: activeProject.id,
        filters: activeFilters,
        selectAll,
        dataSources,
      });
    }, 400);
    return () => {
      if (estimateTimerRef.current) clearTimeout(estimateTimerRef.current);
    };
  }, [activeProject?.id, activeFilters, selectAll, dataSources]);

  // Determine if user has made any content selection
  const hasContentSelected =
    selectAll ||
    activeFilters.rundownIds.length > 0 ||
    activeFilters.eventIds.length > 0 ||
    activeFilters.storyIds.length > 0 ||
    activeFilters.participantIds.length > 0 ||
    activeFilters.tags.length > 0;

  // Functions for chat session management
  const loadChatSessions = async (projectId: string) => {
    try {
      const res = await authFetch(`/api/chat/sessions?projectId=${projectId}`);
      const data = await res.json();
      const sessions: ChatSession[] = (data.sessions || [])
        .slice()
        .sort((a: ChatSession, b: ChatSession) => {
          if (a.is_pinned && !b.is_pinned) return -1;
          if (!a.is_pinned && b.is_pinned) return 1;
          return (
            new Date(b.updated_at || b.created_at).getTime() -
            new Date(a.updated_at || a.created_at).getTime()
          );
        });
      setChatSessions(sessions);
    } catch (err) {
      console.error("Error loading chat sessions:", err);
    }
  };

  const createNewSession = async () => {
    if (!activeProject) return null;
    try {
      const res = await authFetch("/api/chat/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId: activeProject.id }),
      });
      const data = await res.json();
      if (data.session) {
        setChatSessions((prev) => [data.session, ...prev]);
        setActiveSessionId(data.session.id);
        setChatHistory([]);
        return data.session.id;
      }
    } catch (err) {
      console.error("Error creating chat session:", err);
    }
    return null;
  };

  const selectSession = async (sessionId: string) => {
    // Persist current session's messages before switching (idempotent — skips already saved)
    if (activeSessionId && chatHistory.length > 0) {
      try {
        await authFetch(`/api/chat/sessions/${activeSessionId}/messages`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            messages: chatHistory.map((m) => ({
              role: m.role,
              content: m.content,
              citations: m.citations,
              metadata: m.metadata,
              suggestedQuestions: m.suggestedQuestions,
            })),
          }),
        });
      } catch (err) {
        console.error("Error saving messages before switch:", err);
      }
    }

    setActiveSessionId(sessionId);
    try {
      const res = await authFetch(`/api/chat/sessions/${sessionId}/messages`);
      const data = await res.json();
      setChatHistory(
        (data.messages || []).map((m: any) => ({
          role: m.role,
          content: m.content,
          citations: m.citations,
          metadata: m.metadata || undefined,
          suggestedQuestions: m.suggested_questions || undefined,
        })),
      );
    } catch (err) {
      console.error("Error loading messages:", err);
      setChatHistory([]);
    }
    // Refresh sessions list (titles may have changed from auto-title)
    if (activeProject) loadChatSessions(activeProject.id);
  };

  const deleteSession = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDialogData({
      isOpen: true,
      title: "Borrar conversación",
      message:
        "¿Estás seguro de que deseas borrar esta conversación? Esta acción no se puede deshacer.",
      type: "confirm",
      confirmText: "Borrar",
      onConfirm: async () => {
        try {
          await authFetch(`/api/chat/sessions/${sessionId}`, { method: "DELETE" });
          setChatSessions((prev) => prev.filter((s) => s.id !== sessionId));
          if (activeSessionId === sessionId) {
            setActiveSessionId(null);
            setChatHistory([]);
          }
        } catch (err) {
          console.error("Error deleting session:", err);
        }
      },
    });
  };

  const renameSession = async (sessionId: string, newTitle: string) => {
    if (!newTitle.trim()) return;
    try {
      const res = await authFetch(`/api/chat/sessions/${sessionId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: newTitle.trim() }),
      });
      const data = await res.json();
      if (data.session) {
        setChatSessions((prev) =>
          prev.map((s) => (s.id === sessionId ? { ...s, title: newTitle.trim() } : s)),
        );
      }
    } catch (err) {
      console.error("Error renaming session:", err);
    }
    setEditingSessionId(null);
  };

  const togglePinSession = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const session = chatSessions.find((s) => s.id === sessionId);
    if (!session) return;
    const newPinned = !session.is_pinned;
    try {
      await authFetch(`/api/chat/sessions/${sessionId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_pinned: newPinned }),
      });
      setChatSessions((prev) => {
        const updated = prev.map((s) => (s.id === sessionId ? { ...s, is_pinned: newPinned } : s));
        // Re-sort: pinned first, then by updated_at
        return updated.sort((a, b) => {
          if (a.is_pinned && !b.is_pinned) return -1;
          if (!a.is_pinned && b.is_pinned) return 1;
          return (
            new Date(b.updated_at || b.created_at).getTime() -
            new Date(a.updated_at || a.created_at).getTime()
          );
        });
      });
    } catch (err) {
      console.error("Error toggling pin:", err);
    }
  };

  // Wrapped onChatHistoryChange: just update state and refresh titles
  const handleChatHistoryChange = (newHistory: ChatMessage[]) => {
    setChatHistory(newHistory);
    // Refresh sessions list to get updated titles after assistant responds
    if (
      activeProject &&
      newHistory.length > 0 &&
      newHistory[newHistory.length - 1].role === "assistant"
    ) {
      setTimeout(() => loadChatSessions(activeProject.id), 1000);
    }
  };

  // Called by BrainContextPanel before sending a message, ensures a session exists
  const ensureSession = async (): Promise<string | null> => {
    if (activeSessionId) return activeSessionId;
    return await createNewSession();
  };

  const handleInitAI = () => {
    if (!apiKey.trim()) {
      setDialogData({
        isOpen: true,
        title: t("discovery.attention"),
        message: "Please enter a Gemini API key",
        type: "alert",
      });
      return;
    }
    socketService.emit("ai:init", apiKey);
  };

  const toggleFilter = (type: keyof typeof activeFilters, id: string) => {
    setActiveFilters((prev) => ({
      ...prev,
      [type]: prev[type].includes(id) ? prev[type].filter((x) => x !== id) : [...prev[type], id],
    }));
  };

  if (!activeProject) {
    return (
      <div className="discovery-container" data-testid="discovery-container">
        <div className="api-setup-container" data-testid="discovery-no-project">
          <i
            className="bx bx-error-circle"
            style={{ fontSize: "3rem", color: "var(--discovery-accent)", marginBottom: "20px" }}
          ></i>
          <h2>{t("discovery.noActiveProject")}</h2>
          <p style={{ color: "#888" }}>
            Select a project from the side menu to start using Discovery.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="discovery-container"
      data-testid="discovery-container"
      style={isEmbedded ? { height: "100%", position: "relative", width: "100%" } : {}}
    >
      {isCheckingAi && !isAiReady ? (
        <div className="api-setup-container" data-testid="discovery-api-loading">
          <i
            className="bx bx-loader-alt bx-spin"
            style={{ fontSize: "3rem", color: "var(--discovery-accent)", marginBottom: "20px" }}
          ></i>
          <h2>{t("discovery.configTitle")}</h2>
          <p style={{ color: "#888", marginTop: "15px" }}>Verificando configuración...</p>
        </div>
      ) : !isAiReady ? (
        <div className="api-setup-container" data-testid="discovery-api-setup">
          <i
            className="bx bx-brain"
            style={{ fontSize: "3rem", color: "var(--discovery-accent)", marginBottom: "20px" }}
          ></i>
          <h2>{t("discovery.configTitle")}</h2>
          <p
            style={{
              color: "#888",
              textAlign: "center",
              maxWidth: "400px",
              marginTop: "15px",
              lineHeight: "1.5",
            }}
          >
            {t("discovery.aiNotConfigured")}
          </p>
        </div>
      ) : (
        <div
          className={`discovery-content-grid${chatSidebarCollapsed ? " sidebar-collapsed" : ""}`}
          data-testid="discovery-content-grid"
        >
          {/* LEFT PANEL: CONTEXT & SESSIONS */}
          <div
            className={`discovery-panel${chatSidebarCollapsed ? " collapsed" : ""}`}
            data-testid="discovery-sessions-panel"
          >
            {chatSidebarCollapsed ? (
              <button
                className="icon-btn sidebar-toggle-collapsed"
                onClick={toggleChatSidebar}
                title={t("discovery.chatHistory")}
                data-testid="discovery-sidebar-expand-btn"
              >
                <i className="bx bx-chevrons-right"></i>
              </button>
            ) : (
              <div className="tools-scroll-area">
                {/* CHAT SESSIONS */}
                <div className="tool-section" data-testid="discovery-chat-sessions">
                  <div className="tool-header">
                    <span className="tool-title">{t("discovery.chatHistory")}</span>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button
                        className="icon-btn active-plus"
                        onClick={createNewSession}
                        title="New Chat"
                        data-testid="discovery-new-chat-btn"
                      >
                        <i className="bx bx-plus"></i>
                      </button>
                      <button
                        className="icon-btn"
                        onClick={toggleChatSidebar}
                        title="Ocultar barra"
                        data-testid="discovery-sidebar-collapse-btn"
                      >
                        <i className="bx bx-chevrons-left"></i>
                      </button>
                    </div>
                  </div>
                  <div className="sessions-list" data-testid="discovery-sessions-list">
                    {chatSessions.length === 0 && !activeSessionId && (
                      <div className="session-item active">
                        <div className="session-content">
                          <span className="session-title" style={{ opacity: 0.5 }}>
                            {t("discovery.noConversations")}
                          </span>
                          <span className="stats-row" style={{ margin: 0, fontSize: "0.7rem" }}>
                            {t("discovery.sendToStart")}
                          </span>
                        </div>
                      </div>
                    )}
                    {(() => {
                      const pinned = chatSessions.filter((s) => s.is_pinned);
                      const unpinned = chatSessions.filter((s) => !s.is_pinned);
                      const renderSession = (session: ChatSession) => (
                        <div
                          key={session.id}
                          className={`session-item${activeSessionId === session.id ? " active" : ""}${session.is_pinned ? " pinned" : ""}`}
                          onClick={() => selectSession(session.id)}
                          style={{ cursor: "pointer" }}
                          data-testid={`discovery-session-item-${session.id}`}
                        >
                          {session.is_pinned && (
                            <i
                              className="bx bxs-pin"
                              style={{
                                color: "var(--discovery-accent)",
                                fontSize: "0.65rem",
                                marginRight: "2px",
                                flexShrink: 0,
                              }}
                            ></i>
                          )}
                          <div className="session-content" style={{ flex: 1, minWidth: 0 }}>
                            {editingSessionId === session.id ? (
                              <input
                                className="session-rename-input"
                                data-testid="discovery-session-rename-input"
                                value={editingTitle}
                                onChange={(e) => setEditingTitle(e.target.value)}
                                onBlur={() => renameSession(session.id, editingTitle)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") renameSession(session.id, editingTitle);
                                  if (e.key === "Escape") setEditingSessionId(null);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                                style={{
                                  background: "rgba(255,255,255,0.08)",
                                  border: "1px solid var(--discovery-accent)",
                                  borderRadius: "4px",
                                  color: "#fff",
                                  fontSize: "0.8rem",
                                  padding: "2px 6px",
                                  width: "100%",
                                  outline: "none",
                                }}
                              />
                            ) : (
                              <span
                                className="session-title"
                                onDoubleClick={(e) => {
                                  e.stopPropagation();
                                  setEditingSessionId(session.id);
                                  setEditingTitle(session.title);
                                }}
                                title="Doble click para renombrar"
                              >
                                {session.title}
                              </span>
                            )}
                            <span className="stats-row" style={{ margin: 0, fontSize: "0.7rem" }}>
                              {new Date(
                                session.updated_at || session.created_at,
                              ).toLocaleDateString("es-ES", {
                                day: "numeric",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px",
                              flexShrink: 0,
                            }}
                          >
                            <button
                              className="icon-btn"
                              onClick={(e) => togglePinSession(session.id, e)}
                              title={session.is_pinned ? "Desfijar" : "Fijar conversación"}
                              style={{
                                opacity: session.is_pinned ? 0.9 : 0.3,
                                fontSize: "0.7rem",
                                padding: "2px",
                                color: session.is_pinned ? "var(--discovery-accent)" : "inherit",
                              }}
                              data-testid={`discovery-session-pin-${session.id}`}
                            >
                              <i className={`bx ${session.is_pinned ? "bxs-pin" : "bx-pin"}`}></i>
                            </button>
                            <button
                              className="icon-btn"
                              onClick={(e) => deleteSession(session.id, e)}
                              title="Borrar conversación"
                              style={{ opacity: 0.3, fontSize: "0.7rem", padding: "2px" }}
                              data-testid={`discovery-session-delete-${session.id}`}
                            >
                              <i className="bx bx-trash"></i>
                            </button>
                          </div>
                        </div>
                      );
                      return (
                        <>
                          {pinned.length > 0 && (
                            <>
                              <div
                                style={{
                                  fontSize: "0.6rem",
                                  color: "var(--discovery-accent)",
                                  padding: "6px 10px 2px",
                                  letterSpacing: "0.1em",
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <i className="bx bxs-pin" style={{ fontSize: "0.65rem" }}></i>{" "}
                                Pinned
                              </div>
                              {pinned.map(renderSession)}
                              {unpinned.length > 0 && (
                                <div
                                  style={{
                                    borderBottom: "1px solid rgba(255,77,141,0.15)",
                                    margin: "4px 10px",
                                  }}
                                ></div>
                              )}
                            </>
                          )}
                          {unpinned.map(renderSession)}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT PANEL: MAIN DISCOVERY */}
          <div className="discovery-panel" data-testid="discovery-main-panel" ref={containerRef}>
            <div className="discovery-panel-header" data-testid="discovery-panel-header">
              <i className="bx bx-brain discovery-panel-icon"></i>
              <span className="discovery-panel-title">{t("discovery.panelTitle", "BRAIN")}</span>
              <div className="brain-mode-tabs" data-testid="discovery-brain-mode-tabs">
                <button
                  type="button"
                  className={`brain-mode-tab${brainMode === "general" ? " active" : ""}`}
                  onClick={() => setBrainMode("general")}
                  data-testid="discovery-brain-mode-general"
                >
                  <i className="bx bx-file-blank"></i>
                  LOGS / TRANS / OTFs
                </button>
                <button
                  type="button"
                  className={`brain-mode-tab${brainMode === "vtr" ? " active" : ""}`}
                  onClick={() => setBrainMode("vtr")}
                  data-testid="discovery-brain-mode-vtr"
                >
                  <i className="bx bx-movie-play"></i>
                  VTRs
                </button>
              </div>
            </div>

            {/* FILTERS OVERLAY AND CONTEXT CAPACITY WRAPPER */}
            {brainMode === "vtr" ? (
              <div className="brain-vtr-banner" data-testid="discovery-vtr-banner">
                <i className="bx bx-movie-play"></i>
                <div>
                  <div className="brain-vtr-banner-title">
                    Modo VTR — Consulta sobre cortes/VTRs
                  </div>
                  <div className="brain-vtr-banner-subtitle">
                    Se envía el catálogo completo de VTRs del proyecto como contexto. Sin filtros de
                    logs/trans/OTFs.
                  </div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: topPanelHeight ? `${topPanelHeight}px` : "auto",
                  minHeight: "100px",
                  flexShrink: 0,
                }}
              >
                {/* FILTERS OVERLAY */}
                <div
                  className="filters-header"
                  data-testid="discovery-filters-header"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <i
                      className={`bx bx-chevron-${showFilters ? "down" : "right"}`}
                      style={{ color: "var(--discovery-accent)" }}
                    ></i>
                    <span className="tool-title" style={{ color: "#fff" }}>
                      CONTEXT FILTERS
                    </span>
                    <div
                      className="data-source-chips"
                      style={{ marginLeft: "4px" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className={`data-source-chip logs ${dataSources.logs ? "active" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDataSources((prev) => ({ ...prev, logs: !prev.logs }));
                        }}
                        title="Include logs in context"
                        data-testid="discovery-datasource-logs"
                      >
                        <i className="bx bx-file-blank"></i>
                        LOGS
                      </button>
                      <button
                        className={`data-source-chip transcripts ${dataSources.transcripts ? "active" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDataSources((prev) => ({ ...prev, transcripts: !prev.transcripts }));
                        }}
                        title="Include transcriptions in context"
                        data-testid="discovery-datasource-transcripts"
                      >
                        <i className="bx bx-microphone"></i>
                        TRANS
                      </button>
                      <button
                        className={`data-source-chip otfs ${dataSources.otfs ? "active" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDataSources((prev) => ({ ...prev, otfs: !prev.otfs }));
                        }}
                        title="Include OTFs (interviews) in context"
                        data-testid="discovery-datasource-otfs"
                        style={
                          dataSources.otfs
                            ? {
                                background: "rgba(6,182,212,0.15)",
                                borderColor: "rgba(6,182,212,0.4)",
                                color: "#06b6d4",
                              }
                            : {}
                        }
                      >
                        <i className="bx bx-conversation"></i>
                        OTFs
                      </button>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <button
                      className={`reset-filters-btn${selectAll ? " active" : ""}`}
                      data-testid="discovery-select-all-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectAll(!selectAll);
                        if (!selectAll) {
                          setActiveFilters({
                            rundownIds: [],
                            eventIds: [],
                            storyIds: [],
                            participantIds: [],
                            tags: [],
                          });
                        }
                      }}
                      style={
                        selectAll
                          ? {
                              background: "rgba(255, 77, 141, 0.15)",
                              borderColor: "var(--discovery-accent)",
                              color: "var(--discovery-accent)",
                            }
                          : {}
                      }
                    >
                      <i
                        className={`bx ${selectAll ? "bx-check-circle" : "bx-select-multiple"}`}
                      ></i>
                      {selectAll ? t("discovery.allSelected") : t("discovery.selectAll")}
                    </button>
                    {(activeFilters.rundownIds.length > 0 ||
                      activeFilters.storyIds.length > 0 ||
                      activeFilters.participantIds.length > 0 ||
                      activeFilters.tags.length > 0 ||
                      selectAll) && (
                      <button
                        className="reset-filters-btn"
                        data-testid="discovery-reset-filters-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveFilters({
                            rundownIds: [],
                            eventIds: [],
                            storyIds: [],
                            participantIds: [],
                            tags: [],
                          });
                          setSelectAll(false);
                          setContextEstimate(null);
                        }}
                      >
                        <i className="bx bx-x"></i> RESET
                      </button>
                    )}
                  </div>
                </div>

                {showFilters && (
                  <div
                    className="filters-container"
                    data-testid="discovery-filters-container"
                    style={topPanelHeight ? { flex: 1, maxHeight: "none" } : {}}
                  >
                    {/* HIERARCHY TREE */}
                    <div className="filter-group" data-testid="discovery-filter-rundowns">
                      <div className="filter-group-header">
                        <span className="filter-group-title">RUNDOWNS & STORIES</span>
                      </div>
                      <div className="tree-container" data-testid="discovery-tree-container">
                        {filtersData.rundowns
                          .filter((r) => {
                            // No mostrar rundowns programados más allá del día actual
                            const rundownDay =
                              r.date || (r.scheduledAt ? String(r.scheduledAt).split("T")[0] : "");
                            if (!rundownDay) return true;
                            const todayStr = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD local
                            return rundownDay <= todayStr;
                          })
                          .map((r) => {
                            const isRundownActive = activeFilters.rundownIds.includes(r.id);
                            const rundownDay =
                              r.date || (r.scheduledAt ? String(r.scheduledAt).split("T")[0] : "");
                            const rundownDateLabel = rundownDay
                              ? new Date(`${rundownDay}T00:00:00`).toLocaleDateString("es-ES", {
                                  day: "numeric",
                                  month: "short",
                                })
                              : "";
                            return (
                              <div key={r.id} className="tree-item">
                                <div
                                  className={`tree-row tree-row-rundown${isRundownActive ? " tree-row-active" : ""}`}
                                  onClick={(e) => toggleRundownExpand(r.id, e)}
                                >
                                  <i
                                    className={`bx bx-chevron-${expandedRundownIds.includes(r.id) ? "down" : "right"} tree-expand-icon`}
                                  ></i>
                                  <input
                                    type="checkbox"
                                    className="tree-checkbox tree-checkbox-rundown"
                                    checked={isRundownActive}
                                    onChange={() => toggleFilter("rundownIds", r.id)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <i className="bx bx-calendar tree-type-icon tree-icon-rundown"></i>
                                  <span className="tree-label" title={r.name}>
                                    {r.name}
                                  </span>
                                  {rundownDateLabel && (
                                    <span className="tree-rundown-date" title={rundownDay}>
                                      {rundownDateLabel}
                                    </span>
                                  )}
                                </div>
                                {expandedRundownIds.includes(r.id) && (
                                  <div className="tree-children tree-children-rundown">
                                    {filtersData.events
                                      .filter((e) => e.rundownId === r.id)
                                      .map((evt) => {
                                        const isEventActive = activeFilters.eventIds.includes(
                                          evt.id,
                                        );
                                        return (
                                          <div key={evt.id} className="tree-item">
                                            <div
                                              className={`tree-row tree-row-event${isEventActive ? " tree-row-active" : ""}`}
                                              onClick={(e) => toggleEventExpand(evt.id, e)}
                                            >
                                              <i
                                                className={`bx bx-chevron-${expandedEventIds.includes(evt.id) ? "down" : "right"} tree-expand-icon`}
                                              ></i>
                                              <input
                                                type="checkbox"
                                                className="tree-checkbox tree-checkbox-event"
                                                checked={isEventActive}
                                                onChange={() => toggleFilter("eventIds", evt.id)}
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                              <i className="bx bx-bookmark tree-type-icon tree-icon-event"></i>
                                              <span
                                                className="tree-label"
                                                title={evt.title || evt.name}
                                              >
                                                {evt.title || evt.name}
                                              </span>
                                            </div>
                                            {expandedEventIds.includes(evt.id) && (
                                              <div className="tree-children tree-children-event">
                                                {filtersData.stories
                                                  .filter((s) => s.rundownId === evt.id)
                                                  .map((st) => {
                                                    const isStoryActive =
                                                      activeFilters.storyIds.includes(st.id);
                                                    return (
                                                      <div
                                                        key={st.id}
                                                        className={`tree-row tree-row-story${isStoryActive ? " tree-row-active" : ""}`}
                                                      >
                                                        <div style={{ width: "14px" }}></div>
                                                        <input
                                                          type="checkbox"
                                                          className="tree-checkbox tree-checkbox-story"
                                                          checked={isStoryActive}
                                                          onChange={() =>
                                                            toggleFilter("storyIds", st.id)
                                                          }
                                                        />
                                                        <i className="bx bx-book-open tree-type-icon tree-icon-story"></i>
                                                        <span
                                                          className="tree-label"
                                                          title={st.title}
                                                        >
                                                          {st.title}
                                                        </span>
                                                      </div>
                                                    );
                                                  })}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>

                    {/* PARTICIPANTS CLOUD */}
                    <div className="filter-group" data-testid="discovery-filter-participants">
                      <span className="filter-group-title">PARTICIPANTS</span>
                      <div className="filter-cloud" data-testid="discovery-participants-cloud">
                        {filtersData.participants.map((p) => {
                          const isActive = activeFilters.participantIds.includes(p.id);
                          return (
                            <div
                              key={p.id}
                              onClick={() => toggleFilter("participantIds", p.id)}
                              style={{
                                cursor: "pointer",
                                opacity: isActive ? 1 : 0.8,
                                filter: "none",
                                transform: isActive ? "scale(1.05)" : "scale(1)",
                                transition: "all 0.2s ease",
                              }}
                            >
                              <ParticipantBadge
                                participant={p}
                                variant="pill"
                                size="sm"
                                isActive={isActive}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* TAGS CLOUD - Now separates cameras from regular tags */}
                    <div className="filter-group" data-testid="discovery-filter-tags">
                      <span className="filter-group-title">CAMERAS & TAGS</span>
                      <div className="filter-cloud" data-testid="discovery-tags-cloud">
                        {(() => {
                          // 1. Separate Cameras and Tags
                          // Use cameras from filtersData if available, otherwise fallback to allCameras
                          const existingCameraIds = new Set(
                            filtersData.tags.filter((t) => t.isCamera === true).map((t) => t.id),
                          );

                          const cameraTags = [...filtersData.tags].filter(
                            (t) => t.isCamera === true,
                          );
                          // Add missing cameras that might be useful
                          allCameras.forEach((cam) => {
                            if (!existingCameraIds.has(cam.id)) {
                              cameraTags.push({ id: cam.id, label: cam.name, isCamera: true });
                            }
                          });

                          const regularTagsSorted = [...filtersData.tags]
                            .filter((t) => t.isCamera !== true)
                            .sort((a, b) =>
                              (a.label || a.name || a.id).localeCompare(b.label || b.name || b.id),
                            );

                          // 2. Group by actual COLOR to ensure same colors are together
                          const colorGroups: Record<string, any[]> = {};
                          regularTagsSorted.forEach((t) => {
                            const color = getTagColor(t.id);
                            if (!colorGroups[color]) colorGroups[color] = [];
                            colorGroups[color].push(t);
                          });

                          const renderTag = (t: any) => {
                            const isCamera = t.isCamera === true;
                            const displayLabel = t.label || t.name || t.id;
                            const isActive = activeFilters.tags.includes(t.id);
                            const color = isCamera ? "#ffaa00" : getTagColor(t.id);

                            const textColor = isActive
                              ? isColorLight(color)
                                ? "#000"
                                : "#fff"
                              : "#fff";

                            return (
                              <span
                                key={t.id}
                                className={`filter-tag ${isCamera ? "camera" : "tag"} ${isActive ? "active" : ""}`}
                                onClick={() => toggleFilter("tags", t.id)}
                                style={{
                                  borderColor: color,
                                  background: isActive ? color : "transparent",
                                  color: textColor,
                                  opacity: isActive ? 1 : 0.8,
                                }}
                              >
                                {displayLabel}
                              </span>
                            );
                          };

                          return (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                                width: "100%",
                              }}
                            >
                              {/* Cameras Block */}
                              {cameraTags.length > 0 && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "6px",
                                    width: "100%",
                                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                                    paddingBottom: "8px",
                                  }}
                                >
                                  {cameraTags
                                    .sort((a, b) =>
                                      (a.label || a.name).localeCompare(b.label || b.name),
                                    )
                                    .map(renderTag)}
                                </div>
                              )}

                              {/* Color groups sorted by "importance" (basically just iterating through groups) */}
                              {Object.entries(colorGroups).map(([color, items]) => (
                                <div
                                  key={color}
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "6px",
                                    width: "100%",
                                  }}
                                >
                                  {items.map(renderTag)}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {/* CONTEXT CAPACITY BAR */}
                <div className="context-capacity" data-testid="discovery-context-capacity">
                  <div className="context-capacity-header">
                    <span className="context-capacity-label">
                      <i className="bx bx-data" style={{ fontSize: "0.85rem" }}></i>
                      Context Capacity
                    </span>
                    {contextEstimate && contextEstimate.hasSelection ? (
                      <span
                        className="context-capacity-value"
                        style={{
                          color:
                            contextEstimate.percent > 100
                              ? "#ff4d8d"
                              : contextEstimate.percent > 80
                                ? "#ffbe0b"
                                : contextEstimate.percent > 50
                                  ? "#f59e0b"
                                  : "#4ecdc4",
                        }}
                      >
                        {contextEstimate.percent}%
                        <span style={{ color: "#555", marginLeft: "6px" }}>
                          ~
                          {contextEstimate.estimatedTokens >= 1000000
                            ? `${(contextEstimate.estimatedTokens / 1000000).toFixed(1)}M`
                            : `${Math.round(contextEstimate.estimatedTokens / 1000)}K`}{" "}
                          / 1M tokens
                        </span>
                      </span>
                    ) : (
                      <span className="context-capacity-value" style={{ color: "#555" }}>
                        {t("discovery.noSelection")}
                      </span>
                    )}
                  </div>
                  <div className="context-capacity-track">
                    {contextEstimate && contextEstimate.hasSelection && (
                      <div
                        className={`context-capacity-fill ${
                          contextEstimate.percent > 100
                            ? "level-overflow"
                            : contextEstimate.percent > 80
                              ? "level-danger"
                              : contextEstimate.percent > 50
                                ? "level-warning"
                                : "level-safe"
                        }`}
                        style={{ width: `${Math.min(contextEstimate.percent, 100)}%` }}
                      />
                    )}
                  </div>
                  {contextEstimate && contextEstimate.hasSelection ? (
                    <div className="context-capacity-details">
                      {contextEstimate.logs > 0 && (
                        <span>
                          <i
                            className="bx bx-file-blank"
                            style={{ color: "var(--color-data-log)" }}
                          ></i>
                          {contextEstimate.logs.toLocaleString()} logs
                        </span>
                      )}
                      {contextEstimate.segments > 0 && (
                        <span>
                          <i className="bx bx-microphone" style={{ color: "#8B5CF6" }}></i>
                          {contextEstimate.segments.toLocaleString()} trans
                        </span>
                      )}
                      {selectAll && (
                        <span style={{ fontStyle: "italic" }}>
                          <i className="bx bx-globe" style={{ marginRight: "2px" }}></i>
                          {t("discovery.fullProjectSelected")}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="context-capacity-details" style={{ color: "#666" }}>
                      <span>
                        <i className="bx bx-info-circle" style={{ marginRight: "2px" }}></i>
                        {t("discovery.noSelection")}
                      </span>
                    </div>
                  )}
                  {contextEstimate && contextEstimate.percent > 100 && (
                    <div className="context-capacity-warning">
                      <i className="bx bx-error" style={{ fontSize: "0.9rem" }}></i>
                      Content exceeds capacity. Apply filters to reduce context.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* RESIZER SEPARATOR — only meaningful in general mode (filters above) */}
            {brainMode === "general" && (
              <div
                className="resize-separator"
                onMouseDown={handleMouseDown}
                style={{
                  height: "8px",
                  background: "rgba(255, 255, 255, 0)",
                  cursor: "row-resize",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexShrink: 0,
                  zIndex: 10,
                  marginTop: "-4px",
                  marginBottom: "-4px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "4px",
                    background: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "2px",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "var(--discovery-accent)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)")
                  }
                />
              </div>
            )}

            {/* CONTENT AREA */}
            <div className="discovery-main" data-testid="discovery-chat-area">
              {
                <BrainContextPanel
                  onNavigateToLog={onNavigateToLog}
                  dataSources={dataSources}
                  sessionId={activeSessionId}
                  chatHistory={chatHistory}
                  onChatHistoryChange={handleChatHistoryChange}
                  onEnsureSession={ensureSession}
                  disabled={brainMode === "vtr" ? false : !hasContentSelected}
                  hideAiDetails={hideAiDetails}
                  mode={brainMode}
                  activeFilters={{
                    selectAll,
                    eventIds: activeFilters.eventIds,
                    storyIds: activeFilters.storyIds,
                    participantIds: activeFilters.participantIds,
                    tags: activeFilters.tags,
                    rundownIds: activeFilters.rundownIds,
                    rundownNames: activeFilters.rundownIds.map((id) => {
                      const r = filtersData.rundowns.find((r) => r.id === id);
                      return r?.name || id;
                    }),
                    eventNames: activeFilters.eventIds.map((id) => {
                      const e = filtersData.events.find((e) => e.id === id);
                      return e?.title || e?.name || id;
                    }),
                    storyNames: activeFilters.storyIds.map((id) => {
                      const s = filtersData.stories.find((s) => s.id === id);
                      return s?.title || id;
                    }),
                    participantNames: activeFilters.participantIds.map((id) => {
                      const p = filtersData.participants.find((p) => p.id === id);
                      return p?.alias || p?.name || id;
                    }),
                    tagNames: activeFilters.tags.map((id) => {
                      const tagData = filtersData.tags.find((t) => t.id === id);
                      return tagData?.label || tagData?.name || id;
                    }),
                    tagTypes: activeFilters.tags.map((id) => {
                      const tagData = filtersData.tags.find((t) => t.id === id);
                      return tagData?.isCamera ? "camera" : "tag";
                    }),
                  }}
                />
              }
            </div>
          </div>
        </div>
      )}
      {/* SYSTEM OVERLAYS */}
      <CustomDialog
        isOpen={dialogData.isOpen}
        title={dialogData.title}
        message={dialogData.message}
        type={dialogData.type}
        onConfirm={() => {
          if (dialogData.onConfirm) {
            dialogData.onConfirm();
          }
          setDialogData((prev) => ({ ...prev, isOpen: false, onConfirm: undefined }));
        }}
        onCancel={() => setDialogData((prev) => ({ ...prev, isOpen: false, onConfirm: undefined }))}
        confirmText={dialogData.confirmText || t("storyproducer.assembly.understood")}
      />
    </div>
  );
};

export default Discovery;
